import { Money, type Currency } from '@principle-theorem/accounting';
import {
  DepositBookingContext,
  DepositCaptureStrategy,
} from '@principle-theorem/principle-core/interfaces';
import { DAYS_OF_WEEK, type DayOfWeek } from '@principle-theorem/shared';
import { type DepositTreatmentTemplateFormGroup } from './deposit-treatment-template.formgroup';

/**
 * Builds the concise deposit configuration text shown for each treatment row,
 * keeping every display label derived from the same form-state interpretation.
 */
export class DepositTemplateRowSummary {
  private static readonly _captureStrategyShortLabels: Record<
    DepositCaptureStrategy,
    string
  > = {
    [DepositCaptureStrategy.ChargeOnBookingRequest]: 'Charge straight away',
    [DepositCaptureStrategy.CaptureOnBookingRequestApproval]:
      'Charge on acceptance',
    [DepositCaptureStrategy.CaptureOnCancellationOrReschedule]:
      'Hold as cancellation cover',
  };

  private static readonly _bookingContextShortLabels: Record<
    DepositBookingContext,
    string
  > = {
    [DepositBookingContext.Online]: 'Online',
    [DepositBookingContext.StaffNewPatient]: 'New patient',
    [DepositBookingContext.StaffExistingPatient]: 'Existing patient',
  };

  /**
   * Summarises an editable row's deposit settings so the collapsed treatment
   * row exposes the amount, capture strategy, booking types, and days together.
   */
  static summary(
    form: DepositTreatmentTemplateFormGroup,
    currency: Currency
  ): string {
    const value = form.getRawValue();
    return [
      DepositTemplateRowSummary._amountLabel(
        value.depositRequired,
        value.amountValue,
        currency
      ),
      DepositTemplateRowSummary._captureStrategyShortLabels[
        value.captureStrategy
      ],
      DepositTemplateRowSummary._bookingTypesLabel(value.appliesTo),
      DepositTemplateRowSummary._daysLabel(value.depositRequiredDays),
    ]
      .filter((part) => part !== undefined)
      .join(' · ');
  }

  private static _amountLabel(
    depositRequired: boolean,
    amountValue: number | undefined,
    currency: Currency
  ): string | undefined {
    // eslint-disable-next-line eqeqeq -- catches both null (Angular FormControl default) and undefined
    if (amountValue == undefined) {
      return depositRequired ? 'Needs an amount' : undefined;
    }
    return Money.formatPlainCents(Money.toCents(amountValue), currency);
  }

  private static _bookingTypesLabel(
    appliesTo: DepositBookingContext[]
  ): string {
    if (!appliesTo.length) {
      return 'Default booking types';
    }
    return appliesTo
      .map(
        (context) =>
          DepositTemplateRowSummary._bookingContextShortLabels[context]
      )
      .join(' + ');
  }

  private static _dayAbbreviation(day: DayOfWeek): string {
    return day.charAt(0).toUpperCase() + day.slice(1, 3).toLowerCase();
  }

  private static _daysLabel(days: DayOfWeek[]): string {
    if (days.length === DAYS_OF_WEEK.length) {
      return 'Every day';
    }
    const selected = new Set(days);
    return DAYS_OF_WEEK.filter((day) => selected.has(day))
      .map(DepositTemplateRowSummary._dayAbbreviation)
      .join(', ');
  }
}
